import { analisarEscadaApoiada, GRAVIDADE_M_S2, type EntradaEscadaApoiada } from '../dominio/tabletop/estatica'
import type { Rapier } from './motor'

/**
 * Escada apoiada num plano vertical liso, simulada com corpo rígido e atrito
 * de Coulomb. É o mesmo modelo de `analisarEscadaApoiada`: se a simulação e a
 * fórmula discordam, uma das duas está errada (os testes conferem).
 *
 * O resultado vem de rodar a montagem por `DURACAO_S` e olhar se o pé andou. Uma
 * escada que escorrega anda dezenas de centímetros; uma que se sustenta fica
 * dentro de milímetros, por isso o corte em `LIMITE_ESCORREGAMENTO_M` separa os
 * dois casos com folga.
 */

const PASSO_S = 1 / 120
const DURACAO_S = 4
/** A escada tem 2 cm de espessura: as quinas fazem o contato, como o ponto da fórmula. */
const ESPESSURA_M = 0.02
const LARGURA_M = 0.4
/** O pé deslizou mais que isto: a escada escorregou. */
const LIMITE_ESCORREGAMENTO_M = 0.05
/** A escada que desliza também deita: mudar o ângulo mais que isto é escorregar, mesmo com o pé quase parado (escada quase deitada). */
const LIMITE_MUDANCA_ANGULO_GRAUS = 1

export interface EntradaSimulacaoEscada extends EntradaEscadaApoiada {
  readonly comprimentoM: number
  /**
   * O chão real sob a escada, no plano dela: pontos `[distância do pé em direção
   * à parede, altura em relação ao pé]`, em ordem crescente (negativa = atrás do
   * pé). Vira um colisor fixo, e a escada apoia nele. Sem ele, chão plano.
   * `inclinacaoChaoGraus` continua valendo para a fórmula.
   */
  readonly perfilChaoM?: ReadonlyArray<readonly [number, number]>
}

export interface ResultadoSimulacaoEscada {
  readonly escorregou: boolean
  /**
   * A escada está mais deitada que o chão sob o pé (ou paralela a ele): ela ficaria
   * enterrada no terreno, e o teste de "apoiada na parede" não faz sentido.
   * Nesse caso `escorregou` vem verdadeiro e nada é simulado.
   */
  readonly deitadaNoChao: boolean
  /** Quanto o pé andou, em metros (positivo para longe da parede). */
  readonly deslocamentoPeM: number
  readonly anguloFinalGraus: number
}

/** Folga mínima entre o ângulo da escada e o do chão para ela não nascer dentro do terreno. */
export const FOLGA_ESCADA_CHAO_GRAUS = 1

export function simularEscadaApoiada(R: Rapier, entrada: EntradaSimulacaoEscada): ResultadoSimulacaoEscada {
  if (entrada.anguloGraus < (entrada.inclinacaoChaoGraus ?? 0) + FOLGA_ESCADA_CHAO_GRAUS) {
    return { escorregou: true, deitadaNoChao: true, deslocamentoPeM: 0, anguloFinalGraus: entrada.anguloGraus }
  }
  const { anguloGraus, comprimentoM: L, massaEscadaKg, massaCargaKg, posicaoCargaFracao: s, atritoPe: mu } = entrada
  const teta = (anguloGraus * Math.PI) / 180
  const mundo = new R.World({ x: 0, y: -GRAVIDADE_M_S2, z: 0 })
  mundo.timestep = PASSO_S

  // chão com atrito μ (plano, ou o perfil do terreno); parede lisa em x = 0 (a regra Min do par faz o atrito dela, zero, valer)
  const xPe = -L * Math.cos(teta)
  if (entrada.perfilChaoM && entrada.perfilChaoM.length > 0) {
    mundo.createCollider(colisorDoPerfil(R, entrada.perfilChaoM, xPe).setFriction(mu))
  } else {
    mundo.createCollider(R.ColliderDesc.cuboid(100, 0.5, 5).setTranslation(0, -0.5, 0).setFriction(mu))
  }
  mundo.createCollider(
    R.ColliderDesc.cuboid(0.5, 100, 5)
      .setTranslation(0.5, 50, 0)
      .setFriction(0)
      .setFrictionCombineRule(R.CoefficientCombineRule.Min),
  )

  // a escada encosta a quina de baixo no chão e a de cima na parede
  const cx = -(L / 2) * Math.cos(teta) - (ESPESSURA_M / 2) * Math.sin(teta)
  const cy = (L / 2) * Math.sin(teta) + (ESPESSURA_M / 2) * Math.cos(teta)
  const corpo = mundo.createRigidBody(
    R.RigidBodyDesc.dynamic()
      .setTranslation(cx, cy, 0)
      .setRotation({ x: 0, y: 0, z: Math.sin(teta / 2), w: Math.cos(teta / 2) }),
  )
  mundo.createCollider(R.ColliderDesc.cuboid(L / 2, ESPESSURA_M / 2, LARGURA_M / 2).setFriction(mu).setMass(massaEscadaKg), corpo)
  if (massaCargaKg > 0) {
    // a carga é um ponto de massa preso à escada na posição s; sensor, para não tocar em nada
    mundo.createCollider(
      R.ColliderDesc.cuboid(0.02, 0.02, 0.02)
        .setTranslation((s - 0.5) * L, 0, 0)
        .setMass(massaCargaKg)
        .setSensor(true),
      corpo,
    )
  }

  const peInicial = pe(corpo, L, ESPESSURA_M)
  for (let i = 0; i < DURACAO_S / PASSO_S; i += 1) mundo.step()
  const peFinal = pe(corpo, L, ESPESSURA_M)
  const q = corpo.rotation()
  const anguloFinalGraus = Math.abs((2 * Math.atan2(q.z, q.w) * 180) / Math.PI)
  const resultado = {
    escorregou:
      Math.abs(peFinal - peInicial) > LIMITE_ESCORREGAMENTO_M ||
      Math.abs(anguloFinalGraus - anguloGraus) > LIMITE_MUDANCA_ANGULO_GRAUS,
    deitadaNoChao: false,
    deslocamentoPeM: peFinal - peInicial,
    anguloFinalGraus,
  }
  mundo.free()
  return resultado
}

const LARGURA_CHAO_M = 5
/** O chão se estende além do perfil, plano, para a escada não cair pela borda. */
const PROLONGAMENTO_CHAO_M = 100

/**
 * O perfil do terreno como uma superfície de triângulos extrudada em Z. O
 * perfil vem em distância a partir do pé; o pé fica em `xPe`, na altura 0.
 */
function colisorDoPerfil(R: Rapier, perfil: ReadonlyArray<readonly [number, number]>, xPe: number) {
  const pontos: Array<readonly [number, number]> = simplificarPerfil([
    [-PROLONGAMENTO_CHAO_M, perfil[0]![1]],
    ...perfil,
    [PROLONGAMENTO_CHAO_M, perfil[perfil.length - 1]![1]],
  ])
  const vertices: number[] = []
  const indices: number[] = []
  pontos.forEach(([d, y], i) => {
    vertices.push(xPe + d, y, -LARGURA_CHAO_M, xPe + d, y, LARGURA_CHAO_M)
    if (i > 0) {
      const a = 2 * (i - 1)
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2)
    }
  })
  return R.ColliderDesc.trimesh(new Float32Array(vertices), new Uint32Array(indices))
}

const TOLERANCIA_PERFIL_M = 0.002

/**
 * Tira os pontos que estão (a menos de 2 mm) sobre o segmento entre o último
 * ponto mantido e o seguinte. Um trecho reto amostrado a cada 10 cm viraria
 * dezenas de triângulos coplanares, e as costuras entre eles seguram a escada
 * como degraus fantasmas: o chão reto tem que ser um segmento só.
 */
function simplificarPerfil(pontos: ReadonlyArray<readonly [number, number]>): Array<readonly [number, number]> {
  const mantidos: Array<readonly [number, number]> = [pontos[0]!]
  let ancora = 0
  for (let i = 1; i < pontos.length - 1; i += 1) {
    const [x0, y0] = pontos[ancora]!
    const [x1, y1] = pontos[i + 1]!
    // o ponto i continua sobre a reta âncora → i+1 (e os intermediários também)?
    const cabe = pontos.slice(ancora + 1, i + 1).every(([x, y]) => Math.abs(y0 + ((y1 - y0) * (x - x0)) / (x1 - x0) - y) <= TOLERANCIA_PERFIL_M)
    if (!cabe) {
      mantidos.push(pontos[i]!)
      ancora = i
    }
  }
  mantidos.push(pontos[pontos.length - 1]!)
  return mantidos
}

/** Posição horizontal da quina de baixo da escada. */
function pe(corpo: import('@dimforge/rapier3d-compat').RigidBody, L: number, t: number): number {
  const q = corpo.rotation()
  const teta = 2 * Math.atan2(q.z, q.w)
  const p = corpo.translation()
  return p.x - (L / 2) * Math.cos(teta) + (t / 2) * Math.sin(teta)
}

export interface ResultadoLimiteEscada {
  /** O menor ângulo, em graus, em que a simulação deixa a escada parada. */
  readonly anguloMinimoGraus: number
  readonly simulacoes: number
}

/**
 * Procura por bisseção o menor ângulo em que a simulação segura a escada. É a
 * medida que se compara com `anguloMinimoGraus` da fórmula.
 */
export function procurarAnguloMinimoSimulado(
  R: Rapier,
  entrada: Omit<EntradaSimulacaoEscada, 'anguloGraus'>,
  resolucaoGraus = 0.25,
): ResultadoLimiteEscada {
  let baixo = Math.max(5, (entrada.inclinacaoChaoGraus ?? 0) + FOLGA_ESCADA_CHAO_GRAUS + 0.5)
  let alto = 89
  let simulacoes = 0
  const segura = (angulo: number) => {
    simulacoes += 1
    return !simularEscadaApoiada(R, { ...entrada, anguloGraus: angulo }).escorregou
  }
  if (!segura(alto)) return { anguloMinimoGraus: 90, simulacoes }
  if (segura(baixo)) return { anguloMinimoGraus: baixo, simulacoes }
  while (alto - baixo > resolucaoGraus) {
    const meio = (alto + baixo) / 2
    if (segura(meio)) alto = meio
    else baixo = meio
  }
  return { anguloMinimoGraus: (alto + baixo) / 2, simulacoes }
}

